import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check,
  FileText,
  FolderOpen,
  Loader2,
  Pencil,
  Plus,
  X,
} from "lucide-react";
import { useCreateKbSectionWizard } from "../../hooks/useCreateKbSectionWizard";
import { listKbTags } from "../../services/gameRuleKbService";
import { openDraftInHost } from "../../services/hostDocumentService";
import { getSelectionTextPreview } from "../../services/gameRuleWordService";
import type { KbDraftField, KbFieldType } from "../../types/gameRuleKb";
import { KbFieldEditModal } from "./KbFieldEditModal";

type CreationUiStep = "upload" | "section" | "fields";

type FieldModalState =
  | { readonly mode: "add" }
  | { readonly mode: "edit"; readonly fieldId: string };

const stepOrder: readonly CreationUiStep[] = ["upload", "section", "fields"];

const stepCopy: Record<
  CreationUiStep,
  { readonly title: string; readonly subtitle: string }
> = {
  upload: {
    title: "Generate Section Knowledgebase - Step 1 of 3",
    subtitle: "Upload & Open Document",
  },
  section: {
    title: "Generate Section Knowledgebase - Step 2 of 3",
    subtitle: "Select Content & Define Section",
  },
  fields: {
    title: "Generate Section Knowledgebase - Step 3 of 3",
    subtitle: "AI Field Extraction, Review & Save",
  },
};

const KB_FIELD_TYPES: { value: KbFieldType; label: string }[] = [
  { value: "Text", label: "Text" },
  { value: "Number", label: "Number" },
  { value: "Date", label: "Date" },
  { value: "Dropdown", label: "Dropdown" },
];

const isDocxFile = (file: File | null): boolean =>
  Boolean(file?.name.toLowerCase().endsWith(".docx"));

const fileToBase64 = async (file: File): Promise<string> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(
      ...bytes.subarray(offset, offset + chunkSize),
    );
  }
  return btoa(binary);
};

export const CreateKbSectionView = () => {
  const navigate = useNavigate();
  const kb = useCreateKbSectionWizard();
  const [uiStep, setUiStep] = useState<CreationUiStep>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [selectionPreview, setSelectionPreview] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [localStatus, setLocalStatus] = useState<string | null>(null);
  const [isOpeningDocument, setIsOpeningDocument] = useState(false);
  const [fieldModal, setFieldModal] = useState<FieldModalState | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const tagSearchTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (uiStep !== "section") {
      return;
    }
    let disposed = false;
    const refresh = (): void => {
      void getSelectionTextPreview(2000)
        .then((t) => {
          if (!disposed) {
            setSelectionPreview(t);
          }
        })
        .catch(() => {
          if (!disposed) {
            setSelectionPreview("");
          }
        });
    };
    const setup = async (): Promise<void> => {
      if (typeof Office === "undefined") {
        return;
      }
      await Office.onReady();
      if (disposed) {
        return;
      }
      refresh();
      if (Office.context?.document?.addHandlerAsync) {
        Office.context.document.addHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          refresh,
        );
      }
    };
    void setup();
    const iv = window.setInterval(refresh, 1500);
    return () => {
      disposed = true;
      window.clearInterval(iv);
      if (
        typeof Office !== "undefined" &&
        Office.context?.document?.removeHandlerAsync
      ) {
        Office.context.document.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
        );
      }
    };
  }, [uiStep]);

  useEffect(() => {
    if (uiStep !== "section") {
      return;
    }
    if (tagSearchTimerRef.current !== null) {
      window.clearTimeout(tagSearchTimerRef.current);
    }
    tagSearchTimerRef.current = window.setTimeout(() => {
      void listKbTags(tagSearch)
        .then((rows) => {
          const names = rows.map((r) => r.name).filter(Boolean);
          setTagSuggestions(names);
        })
        .catch(() => {
          setTagSuggestions([]);
        });
    }, 300);
    return () => {
      if (tagSearchTimerRef.current !== null) {
        window.clearTimeout(tagSearchTimerRef.current);
      }
    };
  }, [tagSearch, uiStep]);

  const activeIndex = stepOrder.indexOf(uiStep);
  const selectedContent = selectionPreview.trim();
  const displayError = localError || kb.error;

  const progressBar = (
    <div className="-mx-4 -mt-4 border-b border-[var(--border)] bg-background px-3 pt-2">
      <div className="grid grid-cols-3 gap-1">
        {stepOrder.map((step, index) => (
          <div
            key={step}
            className={`h-1 rounded-full ${
              index < activeIndex
                ? "bg-emerald-500"
                : index === activeIndex
                  ? "bg-violet-700"
                  : "bg-muted"
            }`}
          />
        ))}
      </div>
      <div className="px-0 py-4">
        <h2 className="text-sm font-bold leading-tight text-primary">
          {stepCopy[uiStep].title}
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {stepCopy[uiStep].subtitle}
        </p>
        {kb.savedSectionCount > 0 ? (
          <p className="mt-1 text-[11px] text-emerald-700">
            {kb.savedSectionCount} section
            {kb.savedSectionCount === 1 ? "" : "s"} saved
          </p>
        ) : null}
      </div>
    </div>
  );

  const goToSectionStep = async () => {
    setLocalStatus(null);
    if (!file || !isDocxFile(file)) {
      setLocalError("Please choose a .docx file before continuing.");
      return;
    }
    setLocalError(null);
    setIsOpeningDocument(true);
    try {
      const base64 = await fileToBase64(file);
      await openDraftInHost(base64);
      setUiStep("section");
    } catch (err: unknown) {
      setLocalError(
        err instanceof Error
          ? err.message
          : "Unable to open the selected document in Word.",
      );
    } finally {
      setIsOpeningDocument(false);
    }
  };

  const goToFieldsStep = async () => {
    setLocalStatus(null);
    // eslint-disable-next-line no-useless-assignment
    let fullSelection = "";
    try {
      const t = await getSelectionTextPreview(2000);
      fullSelection = t;
    } catch (err: unknown) {
      setLocalError(
        err instanceof Error
          ? err.message
          : "Unable to read the current Word selection.",
      );
      return;
    }
    const validationError = kb.validateStep2(
      fullSelection,
      kb.sectionName,
      kb.selectedTag,
    );
    if (validationError) {
      setLocalError(validationError);
      return;
    }
    setLocalError(null);
    kb.setContentPlain(fullSelection);
    setUiStep("fields");
    await kb.runExtract(fullSelection, kb.sectionName);
  };

  const saveToKnowledgebase = () => {
    setLocalStatus(null);
    setLocalError(null);
    if (fieldModal) {
      setLocalError("Save or cancel the open field editor first.");
      return;
    }
    void (async () => {
      const ok = await kb.saveSection();
      if (ok) {
        const versionLabel =
          kb.lastSavedVersion != null
            ? kb.lastSaveWasVersionUpdate
              ? `Updated to version ${kb.lastSavedVersion}`
              : `Saved as version ${kb.lastSavedVersion}`
            : "Saved section";
        setLocalStatus(
          kb.lastSavedSectionId
            ? `${versionLabel} in Knowledgebase (id: ${kb.lastSavedSectionId}).`
            : `${versionLabel} in Knowledgebase.`,
        );
      }
    })();
  };

  const handleAddAnotherSection = () => {
    kb.resetForAnotherSection();
    setLocalError(null);
    setLocalStatus(null);
    setTagSearch("");
    setTagSuggestions([]);
    setUiStep("section");
  };

  const handleFinish = () => {
    kb.resetForAnotherSection();
    setFile(null);
    navigate("/prizelogic");
  };

  const selectTag = (name: string) => {
    kb.setTag(name);
    setTagSearch("");
  };

  const footer = (
    <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-[var(--border)] bg-background px-3 py-2">
      {kb.showPostSaveActions ? (
        <div className="flex gap-2">
          <button
            type="button"
            className="min-w-0 flex-1 rounded-md border border-violet-300 bg-violet-50 px-3 py-2 text-xs font-bold text-violet-800"
            onClick={handleAddAnotherSection}
          >
            Add another section
          </button>
          <button
            type="button"
            className="min-w-0 flex-1 rounded-md bg-violet-700 px-3 py-2 text-xs font-bold text-white"
            onClick={handleFinish}
          >
            Finish
          </button>
        </div>
      ) : uiStep === "upload" ? (
        <div className="flex gap-2">
          <button
            type="button"
            className="w-[78px] rounded border border-input bg-background px-3 py-2 text-xs font-medium text-foreground"
            onClick={() => navigate("/prizelogic")}
          >
            &larr; Cancel
          </button>
          <button
            type="button"
            className="min-w-0 flex-1 rounded-md bg-violet-700 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            disabled={kb.busy || isOpeningDocument}
            onClick={() => void goToSectionStep()}
          >
            {isOpeningDocument ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Opening...
              </span>
            ) : (
              "Continue \u2192"
            )}
          </button>
        </div>
      ) : uiStep === "section" ? (
        <div className="flex gap-2">
          <button
            type="button"
            className="w-[68px] rounded border border-input bg-background px-3 py-2 text-xs font-medium text-foreground"
            onClick={() => {
              setLocalError(null);
              setUiStep("upload");
            }}
          >
            &larr; Back
          </button>
          <button
            type="button"
            className="min-w-0 flex-1 rounded-md bg-violet-700 px-4 py-2 text-sm font-bold text-white"
            onClick={() => void goToFieldsStep()}
          >
            Extract fields &rarr;
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            type="button"
            className="w-[68px] rounded border border-input bg-background px-3 py-2 text-xs font-medium text-foreground"
            disabled={kb.extractStatus === "loading"}
            onClick={() => {
              setLocalError(null);
              setUiStep("section");
            }}
          >
            &larr; Back
          </button>
          <button
            type="button"
            className="min-w-0 flex-1 rounded-md bg-emerald-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            disabled={kb.busy || kb.extractStatus === "loading"}
            onClick={saveToKnowledgebase}
          >
            {kb.busy ? "Saving..." : "Save to Knowledgebase"}
          </button>
        </div>
      )}
    </div>
  );

  const renderFieldRow = (field: KbDraftField) => (
    <div
      key={field.id}
      className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2 last:border-b-0"
    >
      <FileText
        className="h-4 w-4 shrink-0 text-muted-foreground"
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-bold text-primary">
          {field.name}
          {field.required ? " *" : ""}
        </p>
        <p className="truncate text-[11px] text-muted-foreground">
          {KB_FIELD_TYPES.find((t) => t.value === field.fieldType)?.label ??
            field.fieldType}
          {field.docPlaceholder ? ` — ${field.docPlaceholder}` : ""}
        </p>
      </div>
      {field.isTagged ? (
        <span className="rounded bg-emerald-100 px-2 py-1 text-[10px] font-semibold text-emerald-800">
          Linked
        </span>
      ) : (
        <span className="rounded bg-amber-100 px-2 py-1 text-[10px] font-semibold text-amber-900">
          Not linked
        </span>
      )}
      <button
        type="button"
        className="rounded bg-muted px-2 py-1 text-foreground hover:bg-accent"
        aria-label={`Edit ${field.name}`}
        onClick={() => setFieldModal({ mode: "edit", fieldId: field.id })}
      >
        <Pencil className="h-3.5 w-3.5" aria-hidden />
      </button>
      <button
        type="button"
        className="rounded bg-muted px-2 py-1 text-destructive hover:bg-destructive/10"
        aria-label={`Remove ${field.name}`}
        onClick={() => kb.removeField(field.id)}
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );

  const editingField =
    fieldModal?.mode === "edit"
      ? kb.fields.find((f) => f.id === fieldModal.fieldId)
      : undefined;

  const existingFieldNamesForModal = kb.fields
    .filter((f) => f.id !== editingField?.id)
    .map((f) => f.name);

  const usedDocAnchorsForModal = kb.fields
    .filter((f) => f.id !== editingField?.id && f.docAnchor)
    .map((f) => f.docAnchor as string);

  const uploadStep = (
    <section className="space-y-8 pt-6">
      {isOpeningDocument ? (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-background/85 backdrop-blur-[2px]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="flex flex-col items-center gap-3 rounded-lg border border-[var(--border)] bg-card px-8 py-6 shadow-lg">
            <Loader2
              className="h-9 w-9 animate-spin text-primary"
              aria-hidden
            />
            <p className="text-sm font-medium text-foreground">
              Opening document...
            </p>
          </div>
        </div>
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={(e) => {
          setFile(e.target.files?.[0] ?? null);
          setLocalError(null);
        }}
      />
      <button
        type="button"
        className="mx-auto flex min-h-[170px] w-[88%] max-w-sm flex-col items-center justify-center rounded-md border-2 border-dashed border-violet-300 bg-violet-50/40 px-6 py-6 text-center"
        onClick={() => fileInputRef.current?.click()}
      >
        <FolderOpen
          className="h-9 w-9 text-amber-400"
          fill="currentColor"
          strokeWidth={1.4}
        />
        <p className="mt-3 text-xs text-foreground">
          Click to upload your document.
        </p>
        {file ? (
          <span className="mt-3 max-w-full truncate text-xs font-semibold text-primary">
            {file.name}
          </span>
        ) : null}
      </button>
      {kb.healthStatus === "error" ? (
        <p className="text-xs text-amber-700">
          Game Rule API health check failed. You can still open a document
          locally.
        </p>
      ) : null}
    </section>
  );

  const sectionStep = (
    <section className="space-y-4 pt-3">
      <div className="rounded border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        {selectedContent.length >= 10
          ? `Content selected — ${selectedContent.length} characters — ready for extraction`
          : "Select clause text in Word (at least 10 characters)."}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-bold text-foreground">
          Selected Content (preview)
        </p>
        <p className="text-[11px] text-muted-foreground">
          Preview is shortened for display. The full selection is sent for
          extraction and save.
        </p>
        <div className="max-h-24 overflow-y-auto rounded border border-input bg-muted/40 px-3 py-2 text-xs leading-5 text-foreground shadow-inner">
          {selectedContent ? (
            selectedContent
              .split("\n")
              .map((line, i) => <p key={`${i}-${line}`}>{line}</p>)
          ) : (
            <p className="text-muted-foreground">
              No selection yet — highlight text in Word.
            </p>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div className="border-b border-violet-200 pb-1 text-[11px] font-bold uppercase tracking-wide text-violet-700">
          Section Details
        </div>
        <label
          className="block text-xs font-bold text-foreground"
          htmlFor="kb-section-name"
        >
          Section Name *
          <input
            id="kb-section-name"
            className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
            value={kb.sectionName}
            onChange={(event) => kb.setSectionName(event.target.value)}
          />
        </label>

        <div className="space-y-2">
          <p className="text-xs font-bold text-foreground">Game Type *</p>
          {kb.selectedTag ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-medium text-violet-900">
              {kb.selectedTag}
              <button
                type="button"
                className="text-violet-700"
                aria-label={`Remove tag ${kb.selectedTag}`}
                onClick={kb.clearTag}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ) : null}
          <input
            className="w-full rounded border border-input bg-background px-3 py-2 text-xs"
            placeholder="Search or type a tag..."
            value={tagSearch}
            onChange={(event) => setTagSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && tagSearch.trim()) {
                event.preventDefault();
                selectTag(tagSearch.trim());
              }
            }}
          />
          {tagSuggestions.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {tagSuggestions.slice(0, 8).map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className={`rounded border px-2 py-0.5 text-[11px] ${
                    kb.selectedTag.toLowerCase() === suggestion.toLowerCase()
                      ? "border-violet-500 bg-violet-100 font-semibold text-violet-900"
                      : "border-input bg-background"
                  }`}
                  onClick={() => selectTag(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          ) : null}
          {tagSearch.trim() &&
          !tagSuggestions.some(
            (s) => s.toLowerCase() === tagSearch.trim().toLowerCase(),
          ) ? (
            <button
              type="button"
              className="text-[11px] font-semibold text-violet-700"
              onClick={() => selectTag(tagSearch.trim())}
            >
              Use &quot;{tagSearch.trim()}&quot; as tag
            </button>
          ) : null}
          <p className="text-[11px] text-muted-foreground">
            Exactly one tag is allowed for each section.
          </p>
        </div>

        <label
          className="block text-xs font-bold text-foreground"
          htmlFor="kb-section-description"
        >
          Description
          <textarea
            id="kb-section-description"
            className="mt-1 min-h-[46px] w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
            value={kb.description}
            onChange={(event) => kb.setDescription(event.target.value)}
          />
        </label>
      </div>
    </section>
  );

  const fieldsStep = (
    <section className="space-y-4 pt-3">
      {kb.extractStatus === "loading" ? (
        <div className="flex items-center gap-2 rounded border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Extracting fields with AI...
        </div>
      ) : kb.extractStatus === "error" ? (
        <div className="space-y-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <p>
            Extraction failed. You can add fields manually or go back and retry.
          </p>
          <button
            type="button"
            className="rounded bg-amber-200 px-2 py-1 font-semibold"
            onClick={() => void kb.runExtract(kb.contentPlain, kb.sectionName)}
          >
            Retry extraction
          </button>
        </div>
      ) : (
        <div className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800">
          {kb.fields.length} field{kb.fields.length === 1 ? "" : "s"} — mark
          each as linked before save
        </div>
      )}

      <div className="space-y-2">
        <div className="border-b border-violet-200 pb-1 text-[11px] font-bold uppercase tracking-wide text-violet-700">
          Input Fields
        </div>
        <div className="overflow-hidden rounded border border-[var(--border)] bg-card">
          {kb.fields.map(renderFieldRow)}
        </div>
        <button
          type="button"
          className="flex w-full items-center justify-center gap-1 rounded border border-dashed border-violet-300 bg-violet-50/40 px-3 py-2 text-xs font-semibold text-violet-700"
          onClick={() => setFieldModal({ mode: "add" })}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Add Field Manually
        </button>
      </div>
    </section>
  );

  return (
    <div className="min-h-[calc(100vh-96px)] pb-16">
      {progressBar}
      {displayError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {displayError}
        </div>
      ) : null}
      {localStatus ? (
        <div className="mt-3 flex items-start gap-2 rounded border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {localStatus}
        </div>
      ) : null}
      {uiStep === "upload"
        ? uploadStep
        : uiStep === "section"
          ? sectionStep
          : fieldsStep}
      {fieldModal ? (
        <KbFieldEditModal
          open
          mode={fieldModal.mode}
          initialField={editingField}
          existingFieldNames={existingFieldNamesForModal}
          usedDocAnchors={usedDocAnchorsForModal}
          onCancel={() => setFieldModal(null)}
          onSave={(field) => {
            kb.upsertField(field);
            setFieldModal(null);
          }}
        />
      ) : null}
      {footer}
    </div>
  );
};
